import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Factory,
  Wrench,
  RefreshCcw,
  Building,
  Package,
  Ship,
  CircuitBoard,
  LineChart,
} from "lucide-react";
import { ChevronRight } from "../components/DirectionalIcon";
import { buildLocalizedPath } from "../constants/languages";

const SOLAR_FARM = "https://images.unsplash.com/photo-1647783327914-e7f0db63e263?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHBhbmVscyUyMHBhbGxldHMlMjB3YXJlaG91c2V8ZW58MHx8fHwxNzczNTMxMTMyfDA&ixlib=rb-4.1.0&w=1600&q=85";

const ServiceCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="service-card bg-navy-900 p-8 border border-slate-800 group hover:border-gold/30 transition-colors"
  >
    <div className="w-14 h-14 bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
      <Icon className="text-gold w-7 h-7" strokeWidth={1.5} />
    </div>
    <h3 className="font-outfit text-xl font-semibold text-white mb-3">{title}</h3>
    <p className="text-slate-400 leading-relaxed">{description}</p>
  </motion.div>
);

const KoreaOperationsPage = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.title = t("koreaOperations.meta.title");
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("koreaOperations.meta.description"));
    }
  }, [t]);

  const services = [
    {
      icon: Package,
      title: t("koreaOperations.services.service1.title"),
      description: t("koreaOperations.services.service1.description"),
    },
    {
      icon: Factory,
      title: t("koreaOperations.services.service2.title"),
      description: t("koreaOperations.services.service2.description"),
    },
    {
      icon: RefreshCcw,
      title: t("koreaOperations.services.service3.title"),
      description: t("koreaOperations.services.service3.description"),
    },
    {
      icon: Building,
      title: t("koreaOperations.services.service4.title"),
      description: t("koreaOperations.services.service4.description"),
    },
    {
      icon: Wrench,
      title: t("koreaOperations.services.service5.title"),
      description: t("koreaOperations.services.service5.description"),
    },
    {
      icon: Ship,
      title: t("koreaOperations.services.service6.title"),
      description: t("koreaOperations.services.service6.description"),
    },
    {
      icon: CircuitBoard,
      title: t("koreaOperations.services.service7.title"),
      description: t("koreaOperations.services.service7.description"),
    },
    {
      icon: LineChart,
      title: t("koreaOperations.services.service8.title"),
      description: t("koreaOperations.services.service8.description"),
    },
  ];

  return (
    <div data-testid="korea-operations-page" className="bg-navy">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url(${SOLAR_FARM})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/95 to-navy" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-2 bg-gold text-navy text-xs font-bold uppercase tracking-wider mb-6">
              {t("koreaOperations.hero.badge")}
            </div>
            <h1 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {t("koreaOperations.hero.title")}<br />
              <span className="text-gold">{t("koreaOperations.hero.titleAccent")}</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl">
              {t("koreaOperations.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32 relative">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("koreaOperations.services.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-4">
              {t("koreaOperations.services.title")}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={service.title} {...service} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-20 md:py-32 bg-navy-900">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
                {t("koreaOperations.highlights.kicker")}
              </span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-4 mb-6">
                {t("koreaOperations.highlights.title")}
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8">
                {t("koreaOperations.highlights.description")}
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-navy border border-slate-800">
                  <div className="w-10 h-10 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-gold font-outfit font-bold">01</span>
                  </div>
                  <div>
                    <h4 className="font-outfit font-semibold text-white">{t("koreaOperations.highlights.point1.title")}</h4>
                    <p className="text-slate-400 text-sm">{t("koreaOperations.highlights.point1.description")}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-navy border border-slate-800">
                  <div className="w-10 h-10 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-gold font-outfit font-bold">02</span>
                  </div>
                  <div>
                    <h4 className="font-outfit font-semibold text-white">{t("koreaOperations.highlights.point2.title")}</h4>
                    <p className="text-slate-400 text-sm">{t("koreaOperations.highlights.point2.description")}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-navy border border-slate-800">
                  <div className="w-10 h-10 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-gold font-outfit font-bold">03</span>
                  </div>
                  <div>
                    <h4 className="font-outfit font-semibold text-white">{t("koreaOperations.highlights.point3.title")}</h4>
                    <p className="text-slate-400 text-sm">{t("koreaOperations.highlights.point3.description")}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-navy border border-slate-800 p-8"
            >
              <h3 className="font-outfit text-2xl font-bold text-white mb-6">
                {t("koreaOperations.officeCard.title")}
              </h3>
              <address className="not-italic text-slate-400 leading-relaxed mb-8">
                4385-23 Seodong-daero<br />
                Gongdo-eup, Anseong<br />
                Gyeonggi-do, South Korea
              </address>

              <div className="grid grid-cols-2 gap-6 py-6 border-t border-slate-800">
                <div>
                  <span className="text-gold text-3xl font-outfit font-bold">2000</span>
                  <p className="text-slate-500 text-sm">{t("koreaOperations.officeCard.stat1Label")}</p>
                </div>
                <div>
                  <span className="text-gold text-3xl font-outfit font-bold">24+</span>
                  <p className="text-slate-500 text-sm">{t("koreaOperations.officeCard.stat2Label")}</p>
                </div>
              </div>

              <Link
                to={buildLocalizedPath("/contact", i18n.language)}
                data-testid="korea-contact-btn"
                className="inline-flex items-center gap-2 text-gold font-semibold hover:gap-4 transition-all"
              >
                {t("koreaOperations.officeCard.contactLink")} <ChevronRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 relative noise-overlay">
        <div className="container-custom relative z-10 text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
            {t("koreaOperations.cta.title")}
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-10">
            {t("koreaOperations.cta.subtitle")}
          </p>
          <Link
            to={buildLocalizedPath("/contact", i18n.language)}
            data-testid="korea-cta-btn"
            className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
          >
            {t("koreaOperations.cta.button")}
            <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default KoreaOperationsPage;
